"use client";

import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Configs from "@/config/Configs";
import axios from "axios";

interface CountdownBannerProps {
  countdownData?: string;
}

function isJsonString(str: string): boolean {
  try {
    JSON.parse(str);
  } catch {
    // Removed 'e' since it's unused
    return false;
  }
  return true;
}

const getParsedJson = (json: string, defaultValue: string = "[]") => {
  let parsedJson = defaultValue;

  try {
    if (isJsonString(json)) {
      parsedJson = JSON.parse(json ?? defaultValue);
    }

    if (isJsonString(parsedJson as string)) {
      parsedJson = JSON.parse(parsedJson ?? defaultValue);
    }
  } catch {
    // Removed 'error' since it's unused
    parsedJson = JSON.parse(defaultValue);
  }

  return parsedJson;
};
const CountdownBanner: React.FC<CountdownBannerProps> = () => {
  const { pathname } = useLocation();

  const [parsedData, setParsedData] = useState<{ expiry_date_time: string, heading: string, sub_heading: string, button_link: string, button_label: string } | null>(null);
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  useEffect(() => {
    if (!parsedData) {
      axios.get(`${Configs.API.BASE_URL}/contents/public`, { params: { 'filters[group_name]': Configs.CONTENTS.GROUP_NAME.COUNTDOWN_BANNER, 'filters[url_slug]': pathname } })
        .then((res) => {
          if (res?.data?.payload?.length > 0) {
            const parsedData = getParsedJson(res.data.payload[0].content || "{}") as unknown as { expiry_date_time: string, heading: string, sub_heading: string, button_link: string, button_label: string }
            setParsedData(parsedData);
          }
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }, [parsedData, pathname]);

  useEffect(() => {
    const endDate = new Date(parsedData?.expiry_date_time)
    const calculateTimeLeft = () => {

      const now = new Date();
      const utcNow = Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate(),
        now.getUTCHours(),
        now.getUTCMinutes(),
        now.getUTCSeconds()
      );

      // Ensure endDate is also in UTC
      const utcEndDate = Date.UTC(
        endDate.getUTCFullYear(),
        endDate.getUTCMonth(),
        endDate.getUTCDate(),
        endDate.getUTCHours(),
        endDate.getUTCMinutes(),
        endDate.getUTCSeconds()
      );

      const difference = utcEndDate - utcNow;

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [parsedData]); // Depend on parsedData to handle prop changes

  if (timeLeft.days === 0 && timeLeft.hours === 0 && timeLeft.minutes === 0 && timeLeft.seconds === 0) {
    return <div className="mb-12"></div>
  }

  return (
    <div className="pt-[72px] sticky top-0 left-0 right-0" style={{ zIndex: 1000 }}>
      <div className="bg-gradient-to-r from-[#7E87CF] to-[#9878CF] text-white px-4 py-3 shadow-lg mb-4">
        <div className="max-w-7xl mx-auto">
          {/* Mobile Layout - Stacked */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-center gap-4 lg:gap-8">
            {/* Left side - Text */}
            <div className="flex flex-col text-center lg:text-left">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight mb-2">
                {parsedData?.heading || "Limited time offer"}
              </h2>
              <p className="text-sm sm:text-base lg:text-lg font-normal">
                {parsedData?.sub_heading || "This special sign up page will expire in"}
              </p>
            </div>

            {/* Right side - Countdown timer and Button */}
            <div className="flex flex-col sm:flex-row justify-center items-center gap-4 lg:gap-6">
              {/* Countdown timer */}
              <div className="flex gap-2 sm:gap-3 lg:gap-4">
                {/* Days */}
                <div className="bg-white rounded-lg px-2 py-2 text-center w-14 sm:w-16 lg:w-20 h-16 sm:h-18 lg:h-20 flex flex-col justify-center items-center">
                  <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-black leading-none">
                    {timeLeft.days.toString().padStart(2, '0')}
                  </div>
                  <div className="text-xs sm:text-sm font-normal text-black">
                    DAYS
                  </div>
                </div>

                {/* Hours */}
                <div className="bg-white rounded-lg px-2 py-2 text-center w-14 sm:w-16 lg:w-20 h-16 sm:h-18 lg:h-20 flex flex-col justify-center items-center">
                  <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-black leading-none">
                    {timeLeft.hours.toString().padStart(2, '0')}
                  </div>
                  <div className="text-xs sm:text-sm font-normal text-black">
                    HRS
                  </div>
                </div>

                {/* Minutes */}
                <div className="bg-white rounded-lg px-2 py-2 text-center w-14 sm:w-16 lg:w-20 h-16 sm:h-18 lg:h-20 flex flex-col justify-center items-center">
                  <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-black leading-none">
                    {timeLeft.minutes.toString().padStart(2, '0')}
                  </div>
                  <div className="text-xs sm:text-sm font-normal text-black">
                    MINS
                  </div>
                </div>

                {/* Seconds */}
                <div className="bg-white rounded-lg px-2 py-2 text-center w-14 sm:w-16 lg:w-20 h-16 sm:h-18 lg:h-20 flex flex-col justify-center items-center">
                  <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-black leading-none">
                    {timeLeft.seconds.toString().padStart(2, '0')}
                  </div>
                  <div className="text-xs sm:text-sm font-normal text-black">
                    SECS
                  </div>
                </div>
              </div>

              {/* CTA Button */}
              {parsedData?.button_link && parsedData?.button_label && (
                <a
                  href={parsedData?.button_link || "#"}
                  target="_blank"
                  className="text-sm sm:text-base lg:text-lg font-semibold flex items-center justify-center bg-black text-white w-full sm:w-auto min-w-[200px] sm:min-w-[240px] lg:min-w-[290px] h-12 sm:h-14 lg:h-16 px-4 sm:px-6 py-3 rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap"
                >
                  {parsedData?.button_label || "Claim this offer"}
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CountdownBanner;
